import {
  getAgingRows, getSupplierStatementRows,
  getSupplierStatementColumns,
  getAgingColumns,
  getSupplierLedgerColumns,
  getSupplierCheckColumns,
  getSupplierCheckRows,
  getSupplierOpenItemColumns,
  getSupplierOpenItemRows
} from "./supplier-statement";
import { getReceiptRows } from "./tenant-statement";
import { getSupplierAdvancePaymentRows, getSupplierAdvancePaymentColumns } from "./supplier-advance-payment-statement";
import DynamicObjectDetails from "@/pages/dynamic/detail-views/DynamicObjectDetails";
import {
  getSupplierOpenTransaction, getSupplierOpenItems, getSupplierAging, getSupplierAdvancePayment,
  getSupplierStatementLedger, getSupplierStatementCheck
} from "@/api/finance/reportApi";
import {
  DynamicReport
} from "@/components/dynamic";
import {
  BoxedContent, Header, HeaderLeftContent
} from "@/components/common";

// The sections read top to bottom the way a supplier account is looked at: money paid in advance, what is still open,
// how old it is, then every invoice with where it stands, the ledger's running balance, and last whatever does not add
// up. Each section keeps its own date, so one can be looked at as of a different day without moving the others.
export default function ReportSupplierStatement({ id, activeCompany }) {
  const supplierStatementFilters = {
    SupplierId: id
  };

  // AccountCode is left empty on purpose so the report uses all the supplier payable accounts of the chart. There is
  // no account filter on this statement: a supplier's balance is spread over several payable accounts, and the
  // closing balance has to agree with the Statement Check, which also reads all of them.
  const supplierLedgerFilters = {
    SupplierId: id,
    AccountCode: ""
  };

  const supplierAdvancePaymentFilters = {
    SupplierKey: id,
    PaymentNumber: "*",
    ExpenseNumber: "*",
  };

  const supplierAdvancePaymentFilterOptions = [
    { key: "supplierKey", apiKey: "SupplierKey" },
    { key: "paymentNumber", apiKey: "PaymentNumber", keyType: "string" },
    { key: "expenseNumber", apiKey: "ExpenseNumber", keyType: "string" },
  ];

  return (
    <>
      <DynamicObjectDetails objectName="Supplier" id={id} showHeader={false} showTransfers={false} />
      <BoxedContent>
        <Header
          leftContent={<HeaderLeftContent title="Advance Payments" subtitle="" icon="agent-icon-2" />}
        />
        <DynamicReport
          reportName="suppliers-advance-payments-with-expense"
          activeCompany={activeCompany}
          columnsDataProvider={getSupplierAdvancePaymentColumns}
          rowsDataProvider={getSupplierAdvancePaymentRows}
          fetchData={getSupplierAdvancePayment}
          showDateFilter
          showDateRangeFilter={false}
          exportUrl="procurement/reports/supplier/advance-payments/xls"
          filtersInitialState={supplierAdvancePaymentFilters}
          filterOptions={supplierAdvancePaymentFilterOptions}
          orderBy="AdvancePaymentNumber"
        />
      </BoxedContent>
      <BoxedContent>
        <Header
          leftContent={(
            <HeaderLeftContent
              title="Open Transactions"
              subtitle="Only documents that still have a balance."
              icon="agent-icon-2"
            />
          )}
        />
        <DynamicReport
          reportName="open-transaction"
          activeCompany={activeCompany}
          columnsDataProvider={getSupplierOpenItemColumns}
          rowsDataProvider={getSupplierOpenItemRows}
          fetchData={getSupplierOpenItems}
          showDateFilter
          showDateRangeFilter={false}
          exportUrl="procurement/reports/supplier/open-transactions/xls"
          filtersInitialState={supplierStatementFilters}
          orderBy=""
          isPaginated={false}
        />
      </BoxedContent>
      <BoxedContent>
        <Header
          leftContent={<HeaderLeftContent title="Aging" subtitle="" icon="agent-icon-2" />}
        />
        <DynamicReport
          reportName="aging"
          activeCompany={activeCompany}
          columnsDataProvider={getAgingColumns}
          rowsDataProvider={getAgingRows}
          fetchData={getSupplierAging}
          showDateFilter
          showDateRangeFilter={false}
          exportUrl="procurement/reports/supplier/aging/xls"
          filtersInitialState={supplierStatementFilters}
          orderBy=""
          isPaginated={false}
        />
      </BoxedContent>
      <BoxedContent>
        <Header
          leftContent={(
            <HeaderLeftContent
              title="Invoices"
              subtitle="Every invoice, what settled it, and where it stands."
              icon="agent-icon-2"
            />
          )}
        />
        <DynamicReport
          reportName="supplier-invoices"
          activeCompany={activeCompany}
          columnsDataProvider={getSupplierStatementColumns}
          rowsDataProvider={getSupplierStatementRows}
          fetchData={getSupplierOpenTransaction}
          showDateFilter
          showDateRangeFilter={false}
          exportUrl="procurement/reports/supplier/invoices/xls"
          filtersInitialState={supplierStatementFilters}
          orderBy=""
          isPaginated={false}
        />
      </BoxedContent>
      <BoxedContent>
        <Header
          leftContent={(
            <HeaderLeftContent
              title="Statement of Account"
              subtitle="From the ledger. A positive balance is an amount owed to the supplier."
              icon="agent-icon-2"
            />
          )}
        />
        {/*
          Unpaged on purpose, as on the tenant statement: a statement of account is one document, and its
          running balance and closing balance mean nothing split across pages. Journal lines that carry no
          supplier (manual journals) cannot be placed on a supplier's statement and are not listed here.
        */}
        <DynamicReport
          reportName="supplier-statement"
          activeCompany={activeCompany}
          columnsDataProvider={getSupplierLedgerColumns}
          rowsDataProvider={getReceiptRows}
          fetchData={getSupplierStatementLedger}
          exportUrl="procurement/reports/supplier/statement-of-account/xls"
          filtersInitialState={supplierLedgerFilters}
          orderBy=""
          isPaginated={false}
        />
      </BoxedContent>
      <BoxedContent>
        <Header
          leftContent={(
            <HeaderLeftContent
              title="Statement Check"
              subtitle="Payments and credit notes not applied, and where the documents and the ledger disagree."
              icon="agent-icon-2"
            />
          )}
        />
        <DynamicReport
          reportName="supplier-statement-check"
          activeCompany={activeCompany}
          columnsDataProvider={getSupplierCheckColumns}
          rowsDataProvider={getSupplierCheckRows}
          fetchData={getSupplierStatementCheck}
          showDateFilter
          showDateRangeFilter={false}
          exportUrl="procurement/reports/supplier/statement-check/xls"
          filtersInitialState={supplierStatementFilters}
          orderBy=""
          isPaginated={false}
        />
      </BoxedContent>
    </>
  );
}
